import qz from 'qz-tray';
import { useCallback, useEffect, useRef, useState } from 'react';
import config from 'src/config';

export interface QzTrayState {
  isConnected: boolean;
  isConnecting: boolean;
  printerList: string[];
  defaultPrinter: string;
  error: string | null;
}

let globalConnected = false;
let globalPrinterList: string[] = [];
let globalInitialized = false;
let globalSecuritySetup = false;

const QZ_UNAVAILABLE_KEY = 'qz_unavailable';
const ONE_HOUR = 60 * 60 * 1000;

export const getQzConnected = (): boolean => globalConnected;

export const useQzTray = () => {
  const [state, setState] = useState<QzTrayState>({
    isConnected: globalConnected,
    isConnecting: false,
    printerList: globalPrinterList,
    defaultPrinter: '',
    error: null,
  });

  const isMounted = useRef(true);

  const safeSetState = useCallback(
    (updater: Partial<QzTrayState> | ((prev: QzTrayState) => Partial<QzTrayState>)) => {
      if (!isMounted.current) return;
      setState(prev => ({
        ...prev,
        ...(typeof updater === 'function' ? updater(prev) : updater),
      }));
    },
    []
  );

  // ── 보안 설정 ──────────────────────────────────
  const setupSecurity = useCallback(() => {
    qz.security.setSignatureAlgorithm('SHA512');

    qz.security.setCertificatePromise(
      (resolve: (cert: string) => void, reject: (err: unknown) => void) => {
        fetch(`${config.apiUrl}/label/certificate`)
          .then(res => {
            if (!res.ok) throw new Error(`인증서 조회 실패: ${res.status}`);
            return res.text();
          })
          .then(cert => resolve(cert))
          .catch(err => reject(err));
      }
    );

    qz.security.setSignaturePromise((toSign: string) => {
      return (resolve: (val: string) => void, reject: (err: unknown) => void) => {
        const token = localStorage.getItem('access-token');
        fetch(`${config.apiUrl}/label/sign`, {
          method: 'POST',
          headers: {
            'Content-Type': 'text/plain',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: toSign,
        })
          .then(res => {
            if (!res.ok) throw new Error(`서명 실패: ${res.status}`);
            return res.text();
          })
          .then(sig => resolve(sig))
          .catch(err => reject(err));
      };
    });
  }, []);

  // ── 연결 ───────────────────────────────────────
  const connect = useCallback(async (): Promise<boolean> => {
    const failedAt = Number(localStorage.getItem(QZ_UNAVAILABLE_KEY) || 0);
    if (failedAt && Date.now() - failedAt < ONE_HOUR) {
      globalConnected = false;
      safeSetState({ isConnected: false, isConnecting: false });
      return false;
    }

    if (qz.websocket.isActive()) {
      const printers = await qz.printers.find();
      const defaultPrinter = await qz.printers.getDefault();
      globalConnected = true;
      globalPrinterList = printers;
      safeSetState({
        isConnected: true,
        isConnecting: false,
        printerList: printers,
        defaultPrinter,
        error: null,
      });
      return true;
    }

    safeSetState({ isConnecting: true, error: null });

    try {
      await qz.websocket.connect({
        retries: 0,
        delay: 1,
        host: ['localhost'],
        port: { secure: [8181], insecure: [8182] },
      });

      localStorage.removeItem(QZ_UNAVAILABLE_KEY);

      const printers: string[] = await qz.printers.find();
      const defaultPrinter: string = await qz.printers.getDefault();

      globalConnected = true;
      globalPrinterList = printers;
      safeSetState({
        isConnected: true,
        isConnecting: false,
        printerList: printers,
        defaultPrinter,
        error: null,
      });

      console.log('[QzTray] 연결 완료, 프린터 목록:', printers);
      return true;
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'QZ Tray 연결 실패';
      localStorage.setItem(QZ_UNAVAILABLE_KEY, String(Date.now()));
      globalConnected = false;
      safeSetState({ isConnected: false, isConnecting: false, error: message });
      console.log('[QzTray] 미연결 (설치되지 않았거나 실행 중이 아님)');
      return false;
    }
  }, [safeSetState]);

  // ── 수동 재연결 ────────────────────────────────
  const retryConnect = useCallback(async (): Promise<boolean> => {
    localStorage.removeItem(QZ_UNAVAILABLE_KEY);
    globalInitialized = false;
    return connect();
  }, [connect]);

  // ── 마운트 시 자동 연결 ────────────────────────
  useEffect(() => {
    isMounted.current = true;
    if (!globalSecuritySetup) {
      globalSecuritySetup = true;
      setupSecurity();
    }
    if (!globalInitialized) {
      globalInitialized = true;
      connect();
    }
    return () => {
      isMounted.current = false;
    };
  }, []);

  const refreshPrinters = useCallback(async () => {
    if (!qz.websocket.isActive()) return;
    try {
      const printers: string[] = await qz.printers.find();
      const defaultPrinter: string = await qz.printers.getDefault();
      globalPrinterList = printers;
      safeSetState({ printerList: printers, defaultPrinter });
    } catch (err) {
      console.error('[QzTray] 프린터 목록 조회 실패:', err);
    }
  }, [safeSetState]);

  return { ...state, connect, retryConnect, refreshPrinters };
};
